import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { consultationService } from '../services/consultation.service'
import { ConsultationCreate, ConsultationUpdate } from '../types'

export const useConsultations = () => {
  const queryClient = useQueryClient()

  // After any state change to a note, refresh every derived view from source
  // (REQ-FE-07): the per-patient timeline + trends, the derived clinical
  // summary (summary tab, active-consultation context panel, header pin), and
  // the individual note cache. Partial keys match across all patient ids.
  const invalidateDerivedViews = () => {
    queryClient.invalidateQueries({ queryKey: ['consultation'] })
    queryClient.invalidateQueries({ queryKey: ['consultations-by-patient'] })
    queryClient.invalidateQueries({ queryKey: ['clinical-summary'] })
  }

  const useConsultation = (id: string) => {
    return useQuery({
      queryKey: ['consultation', id],
      queryFn: () => consultationService.getConsultation(id),
      enabled: !!id,
    })
  }

  const useConsultationByAppointment = (appointmentId: string) => {
    return useQuery({
      queryKey: ['consultation-by-appointment', appointmentId],
      queryFn: () => consultationService.getByAppointment(appointmentId),
      enabled: !!appointmentId,
    })
  }

  const useCreateConsultation = () => {
    return useMutation({
      mutationFn: (data: ConsultationCreate) => consultationService.createConsultation(data),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['consultation-by-appointment'] })
      },
    })
  }

  const useUpdateConsultation = () => {
    return useMutation({
      mutationFn: ({ id, data }: { id: string; data: ConsultationUpdate }) =>
        consultationService.updateConsultation(id, data),
      onSuccess: () => {
        // A saved draft can change the draft list/count surfaced per patient.
        invalidateDerivedViews()
      },
    })
  }

  const useCompleteConsultation = () => {
    return useMutation({
      mutationFn: (id: string) => consultationService.completeConsultation(id),
      // Signing publishes the note into the immutable record: timeline, trends,
      // and the derived summary must all refresh from source (REQ-FE-07).
      onSuccess: () => invalidateDerivedViews(),
    })
  }

  const useRequestUnlock = () => {
    return useMutation({
      mutationFn: ({ id, reason }: { id: string; reason: string }) =>
        consultationService.requestUnlock(id, reason),
      onSuccess: () => invalidateDerivedViews(),
    })
  }

  const useApproveUnlock = () => {
    return useMutation({
      mutationFn: (id: string) => consultationService.approveUnlock(id),
      onSuccess: () => invalidateDerivedViews(),
    })
  }

  return {
    useConsultation,
    useConsultationByAppointment,
    useCreateConsultation,
    useUpdateConsultation,
    useCompleteConsultation,
    useRequestUnlock,
    useApproveUnlock,
  }
}
